import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridToolbarExport,
} from '@mui/x-data-grid';
import CustomNoRowsOverlay from './CustomNoRowsOverlay';
import PropTypes from 'prop-types';
import { GRID_DEFAULT_LOCALE_TEXT } from './localeTextConstants';
import { CustomPagination } from './CustomPagination';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';

/**
 * MUI x-data-grid komponenta za tabelarni prikaz podataka
 *
 * Props:
 *
 * `data` niz podataka za prikaz **Required**
 *
 * `loading` boolean state, indikator učitavanja podataka **Required**
 *
 * `columns` niz objekata sa specificnim key-evima definisanim u propTypes (pogledati) **Required**
 *
 * `checkboxSelection` boolean tip, šalje se kada nam je potreban checkbox u gridu za selektovanje redova
 *
 * `disableRowClick` boolean tip, šalje se kada je `checkboxSelection` true kako se select reda ne bi radio i na check i na row click
 *
 * `onRowClick` funkcija za hendlovanje row click-a, šalje se u koliko nam je potreban selektovani red, prima
 * njegov id, međutim u koliko je `checkboxSelection` true onda će primiti niz id-eva selektovanih redova
 *
 * `onRowDoubleClick` funkcija za hendlovanje duplog klika na red, menja `onRowClick` funkciju u koliko ona
 * služi da hendluje `checkboxSelection`
 *
 * `...rest` po potrebi sluzi za dodavanje drugih propova DataGrid komponente
 */

function CustomToolbar(props, data, columns) {
  const { t, i18n } = useTranslation();
  // Prebacivanje logike za konfiguraciju kolona unutar MuiGrid
  const excelColumnsConfig = useMemo(
    () => ({
      columnNames: columns.map((col) => col.headerName),
      keys: columns.map((col) => col.field),
      fileName: 'Excel fajl.xlsx',
      sheetName: 'Podaci',
    }),
    [columns],
  );
  const handleExport = () => {
    const dataForExport = data.map((row) => {
      const newRow = {};
      excelColumnsConfig.keys.forEach((key) => {
        newRow[key] = row[key];
      });
      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExport);
    XLSX.utils.sheet_add_aoa(worksheet, [excelColumnsConfig.columnNames], {
      origin: 'A1',
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      excelColumnsConfig.sheetName,
    );
    XLSX.writeFile(workbook, excelColumnsConfig.fileName);
  };
  return (
    <GridToolbarContainer {...props}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <button
        onClick={handleExport}
        style={{ color: '#1976d2', fontWeight: 600 }}
      >
        {t('downloadExel')}
      </button>
      {/* <GridToolbarQuickFilter style={{ marginLeft: 'auto' }} /> */}
    </GridToolbarContainer>
  );
}

function CustomToolbar2(props) {
  // Prebacivanje logike za konfiguraciju kolona unutar MuiGrid
  // const excelColumnsConfig = useMemo(
  //   () => ({
  //     columnNames: columns.map((col) => col.headerName),
  //     keys: columns.map((col) => col.field),
  //     fileName: 'Excel fajl.xlsx',
  //     sheetName: 'Podaci',
  //   }),
  //   [columns],
  // );
  // const handleExport = () => {
  //   const dataForExport = data.map((row) => {
  //     const newRow = {};
  //     excelColumnsConfig.keys.forEach((key) => {
  //       newRow[key] = row[key];
  //     });
  //     return newRow;
  //   });

  //   const worksheet = XLSX.utils.json_to_sheet(dataForExport);
  //   XLSX.utils.sheet_add_aoa(worksheet, [excelColumnsConfig.columnNames], {
  //     origin: 'A1',
  //   });

  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(
  //     workbook,
  //     worksheet,
  //     excelColumnsConfig.sheetName,
  //   );
  //   XLSX.writeFile(workbook, excelColumnsConfig.fileName);
  // };
  return (
    <GridToolbarContainer {...props}>
      {/* <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <button
        onClick={handleExport}
        style={{ color: '#1976d2', fontWeight: 600 }}
      >
        Preuzmi Excel
      </button>
      <GridToolbarQuickFilter style={{ marginLeft: 'auto' }} /> */}
    </GridToolbarContainer>
  );
}

const MuiGrid = ({
  data,
  loading,
  columns,
  checkboxSelection,
  disableRowClick,
  onRowClick,
  onRowDoubleClick,
  isRowSelectable,
  pageSizeOption,
  hideFooter,
  density,
  getRowClassName,
  rowSelectionModel,
  toolbar2 = false,
  onSearchChange, // funkcija za filter
  savedFilter = '', // sacuvan filter
  ...rest
}) => {
  const { t, i18n } = useTranslation();
  // enables quick filter by all columns
  const _columns = useMemo(
    () =>
      columns.filter((column) =>
        columns.map((item) => item.field === column.field),
      ),
    [columns],
  );
  const [searchValueGrid, setSearchValueGrid] = useState('');
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    if (savedFilter !== undefined) {
      setSearchValueGrid(savedFilter);
    }
  }, [savedFilter]);
  const handleSearchChange = (event) => {
    const newValue = event.target.value;
    setSearchValueGrid(newValue);
    if (typeof onSearchChange === 'function') {
      onSearchChange(newValue); // Obaveštavanje parent komponente
    }
  };

  const deleteFilter = () => {
    setSearchValueGrid('');
    if (inputRef.current !== null) {
      inputRef.current.focus();
    }
    if (typeof onSearchChange === 'function') {
      onSearchChange('');
    }
  };

  // Filtriranje podataka prema unetoj vrednosti
  const filteredData = useMemo(() => {
    if (!searchValueGrid) return data;
    const lowercasedSearchValue = searchValueGrid.toLowerCase();

    return data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(lowercasedSearchValue),
      ),
    );
  }, [data, searchValueGrid]);

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      {!toolbar2 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            position: 'absolute',
            right: '6px',
            zIndex: 1,
            padding: '5px',
            borderBottom: '1px solid #8f8f8f',
            backgroundColor:
              searchValueGrid !== ''
                ? '#fff77a'
                : isFocused
                ? '#f0f8ff'
                : 'white',
            borderRadius: '0px',
            width: '180px',
            top: '1px',
          }}
        >
          {/* Ikonica pretrage */}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span
              style={{
                marginRight: '5px',
                color: '#aaa',
                fontSize: '14px',
              }}
            >
              🔍
            </span>
            {/* Polje za unos */}
            <input
              type="text"
              placeholder="Pretraži..."
              value={searchValueGrid}
              onChange={(e) => handleSearchChange(e)}
              onFocus={() => setIsFocused(true)} // Kada dobije fokus
              onBlur={() => setIsFocused(false)} // Kada izgubi fokus
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '15px',
                backgroundColor:
                  searchValueGrid !== ''
                    ? '#fff77a'
                    : isFocused
                    ? '#f0f8ff'
                    : 'white',
                width: '170px',
              }}
              ref={inputRef}
            />
            {/* Dugme za brisanje vrednosti */}
            <div style={{ marginRight: '10px' }}>
              {searchValueGrid && (
                <button
                  onClick={deleteFilter}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#aaa',
                    fontSize: '12px',
                    marginLeft: '0px',
                    width: '0px',
                  }}
                >
                  ❌
                </button>
              )}{' '}
            </div>
          </div>
        </div>
      )}
      <DataGrid
        loading={loading}
        rows={filteredData}
        columns={_columns}
        getRowClassName={getRowClassName}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: pageSizeOption || 25,
            },
          },
        }}
        pageSizeOptions={pageSizeOption ? [25, 50, 100] : [5, 10, 25, 50, 100]}
        checkboxSelection={false || checkboxSelection}
        disableRowSelectionOnClick={false || disableRowClick}
        onRowSelectionModelChange={undefined || onRowClick}
        onRowDoubleClick={undefined || onRowDoubleClick}
        isRowSelectable={undefined || isRowSelectable}
        hideFooter={hideFooter}
        density={density || 'compact'}
        slots={{
          toolbar: (props) =>
            toolbar2
              ? CustomToolbar2(props)
              : CustomToolbar(props, data, columns),
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            showquickfilter: 'true',
            quickfilterprops: { debounceMs: 500 },
          },
          pagination: {
            ActionsComponent: CustomPagination,
          },
        }}
        sx={{
          '.MuiDataGrid-cell': {
            borderRight: '1px solid rgba(224, 224, 224, 1)',
          },
        }}
        localeText={i18n.language === 'SRB' ? GRID_DEFAULT_LOCALE_TEXT : null}
        rowSelectionModel={rowSelectionModel}
        {...rest}
      />
    </Box>
  );
};

export default MuiGrid;

MuiGrid.propTypes = {
  /**
   * `array` required array of data
   */
  data: PropTypes.array.isRequired,
  /**
   * `true` required boolean prop witch indicates that data is fetching
   *
   * `false` required boolean prop with indicates that data is fetched
   */
  loading: PropTypes.bool.isRequired,
  /**
   * `array` required array of objects to display
   */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string.isRequired,
      description: PropTypes.string,
      width: PropTypes.number,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      renderCell: PropTypes.func,
      valueFormatter: PropTypes.func,
      valueGetter: PropTypes.func,
      type: PropTypes.string,
      disableExport: PropTypes.bool,
    }).isRequired,
  ),
  /**
   * `true` boolean prop that indicates usage of checkbox for row selection
   *
   * `false` boolean prop that indicates usage of checkbox for row selection
   *
   * @default 'false'
   */
  checkboxSelection: PropTypes.bool,
  /**
   * `true` boolean prop that disables row click
   *
   * `false` boolean prop that enables row click
   *
   * **Note:** this property is required if checkboxSelection is true
   *
   * @default 'false'
   */
  disableRowClick: PropTypes.bool,
  /**
   * `undefined` undefined if not pased
   *
   * `function` row click handler function, if checkboxSelection is false, selecting one row returns
   * that row's id, if checkboxSelection is true returns id array of selected rows
   *
   * **Note:** this property is required if checkboxSelection is true
   *
   * @default 'undefined'
   */
  onRowClick: PropTypes.func,
  /**
   * `undefined` undefined if not pased
   *
   * `function` row double click handler function
   *
   * **Note:** this property is required if checkboxSelection is true
   *
   * @default 'undefined'
   */
  onRowDoubleClick: PropTypes.func,
  /**
   * include other prop types from DataGrid component
   */
  ...DataGrid.propTypes,
};
